import { ConnatixConfig, ConnatixPlayerApi } from './connatixTypes'
import {
    sendVideoImpressionAction,
    sendVideoAction,
} from '@/utils/adEngineEvents'
import {
    createConnatixTrack,
    handleAdsTracking,
} from '@/connatix/connatixTracking'
import { EVENT_CATEGORIES } from '@/utils/tracking/videoTracking'
import { createConnatixVideoTracker } from '@/connatix/initializeConnatix'

export interface VideoAdsOptions {
    showAds: boolean
    targetingParams?: string
    videoAdUnitPath?: string
    vastXml?: string
}

export const VAST_XML_LINE_ITEM_ID = '1683802b-bbc3-453b-8fbc-f0ac1f9d3360'
export const GAM_TAG_URL_TEMPLATE =
    'https://pubads.g.doubleclick.net/gampad/live/ads?iu=[path]&description_url=[PAGE_URL]&tfcd=0&npa=0&sz=400x300%7C640x480%7C1280x720%7C1920x1080&gdfp_req=1&output=vast&env=vp&unviewed_position_start=1&impl=s&correlator=[CB]&cust_params=[custmac]&vpmute=[MUTE]'
function handleTakeoverSynchronizer(
    playerApi: ConnatixPlayerApi,
    connatixElement: HTMLElement,
) {
    playerApi.on('adImpression', (data) => {
        if (!data.adSourceData?.imaMetadata?.adsManager) {
            return
        }

        const { adId, creativeId } =
            data.adSourceData.imaMetadata.adsManager?.getCurrentAd?.().data ??
            {}

        if (adId && creativeId) {
            connatixElement.dataset.dataVastLineItemId = adId
            connatixElement.dataset.dataVastCreativeId = creativeId
            sendVideoImpressionAction(adId, creativeId)
        }
    })

    playerApi.on('play', () => {
        sendVideoAction('play')
    })

    playerApi.on('playError', () => {
        sendVideoAction('playError')
    })

    playerApi.on('adError', () => {
        sendVideoAction('adError')
    })
}

function applyRVParameter(targetingParams: string, adIndex: number) {
    const newTargetingParams = new URLSearchParams(targetingParams)
    newTargetingParams.set('rv', adIndex.toString())

    return newTargetingParams.toString()
}

export function handleAds(
    playerApi: ConnatixPlayerApi,
    connatixElement: HTMLElement,
    { showAds, videoAdUnitPath, targetingParams, vastXml }: VideoAdsOptions,
) {
    if (showAds && targetingParams && videoAdUnitPath) {
        let adIndex = 1
        const videoAdsTracker = createConnatixVideoTracker(
            playerApi.getApiConfig(),
            EVENT_CATEGORIES.AD,
        )
        const track = createConnatixTrack(
            connatixElement.id,
            playerApi,
            videoAdsTracker,
        )
        playerApi.setMacros({
            custmac: applyRVParameter(targetingParams, adIndex),
            path: videoAdUnitPath,
        })

        handleTakeoverSynchronizer(playerApi, connatixElement)
        handleAdsTracking(playerApi, track)

        playerApi.on('adImpression', () => {
            adIndex++

            playerApi.setMacros({
                custmac: applyRVParameter(targetingParams, adIndex),
            })
        })

        // if vastXml has been used, we need to set line item metadata with GAM tag url for the next ad
        if (vastXml) {
            playerApi.once('play', () => {
                playerApi.setLineItemMetadata({
                    id: VAST_XML_LINE_ITEM_ID,
                    url: GAM_TAG_URL_TEMPLATE,
                })
            })
        }
    }
}

export function getAdsSettings({
    showAds,
    vastXml,
}: VideoAdsOptions): ConnatixConfig['settings'] {
    const adsSettings: ConnatixConfig['settings'] = {
        disableAdvertising: !showAds,
    }

    if (showAds) {
        const preRollBreak =
            Number(
                new URLSearchParams(window.location.search).get(
                    'cnxPreRollBreak',
                ),
            ) || undefined

        adsSettings.advertising = {
            preRollBreak,
            lineItems: [
                {
                    id: VAST_XML_LINE_ITEM_ID,
                    vastXml,
                    url: !vastXml ? GAM_TAG_URL_TEMPLATE : undefined,
                },
            ],
        }
    }

    return adsSettings
}
